import { useQuery } from "@apollo/client";
import { Icon } from "leaflet";
import "leaflet/dist/leaflet.css";
import { ChangeEvent, useState } from "react";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  ZoomControl,
} from "react-leaflet";
import { Link } from "react-router";
import { GetPlaceTypes } from "../../api/place-types";
import { GetPlaces } from "../../api/places";
import GrainMill from "../../assets/images/grain-mill.svg?no-inline";
import House from "../../assets/images/house.svg?no-inline";
import Oven from "../../assets/images/oven.svg?no-inline";
import SacralBuilding from "../../assets/images/sacral-building.svg?no-inline";
import Image from "../../components/core/Image/Image";
import Checkbox from "../../components/form/Checkbox/Checkbox";
import { translate } from "../../localization";
import styles from "./Map.module.scss";

export enum PlaceType {
  House = "cln4kxgns0056pc7kbwcpfrs1",
  SacralBuilding = "clnhmhms5001kpe7krw5imga7",
  GrainMill = "clteq7qfh00eiob7mp0wf31e1",
  Oven = "clteqf1ei00eqob7mtilzshq4",
}

const Map = () => {
  const i18n = translate();
  const [filter, setFilter] = useState<PlaceType[]>([]);
  const { data: placeTypes } = useQuery(GetPlaceTypes, {
    variables: {
      where: {
        id: {
          in: [
            "cln4kxgns0056pc7kbwcpfrs1",
            "clteqf1ei00eqob7mtilzshq4",
            "clnhmhms5001kpe7krw5imga7",
            "clteq7qfh00eiob7mp0wf31e1",
          ],
        },
      },
    },
  });

  const { data } = useQuery(GetPlaces, {
    variables: {
      hydrateRelationships: true,
      where: {
        AND: [],
        gps: {
          not: { equals: "" },
        },
        typen: {
          some: {
            id: {
              in: filter,
            },
          },
        },
      },
    },
  });

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (filter.includes(value as PlaceType)) {
      setFilter(filter.filter((item) => item !== (value as PlaceType)));
    } else {
      setFilter([...filter, value as PlaceType]);
    }
  };

  const houseIcon = new Icon({
    iconSize: [25, 25],
    iconUrl: House,
  });

  const grainMillIcon = new Icon({
    iconSize: [27, 22],
    iconUrl: GrainMill,
  });

  const ovenIcon = new Icon({
    iconSize: [31, 38],
    iconUrl: Oven,
  });

  const sacralBuildingIcon = new Icon({
    iconSize: [19, 31],
    iconUrl: SacralBuilding,
  });

  const getCategoryIcon = (types: PlaceType[]) => {
    if (types.includes(PlaceType.GrainMill)) {
      return grainMillIcon;
    } else if (types.includes(PlaceType.SacralBuilding)) {
      return sacralBuildingIcon;
    } else if (types.includes(PlaceType.Oven)) {
      return ovenIcon;
    }
    return houseIcon;
  };

  return (
    <div className={styles.mapWrapper}>
      {placeTypes?.items && (
        <div className={styles.filters}>
          {placeTypes.items.map((placeType) => (
            <Checkbox
              inverse
              id={placeType.id}
              key={placeType.id}
              label={placeType.name ?? ""}
              onChange={handleFilterChange}
              checked={filter.includes(placeType.id as PlaceType)}
            />
          ))}
        </div>
      )}
      <MapContainer
        zoom={14}
        zoomControl={false}
        className={styles.map}
        scrollWheelZoom={false}
        attributionControl={false}
        center={[46.803117852591726, 11.239487225294644]}
      >
        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
        {data?.items?.map((item) => {
          const [latitude, longitude] = item.gps
            ?.split(",")
            .map((num) => Number(num.trim())) ?? [0, 0];
          return (
            <Marker
              key={item.id}
              position={[latitude ?? 0, longitude ?? 0]}
              icon={getCategoryIcon(
                item.typen?.map((type) => type.id as PlaceType) ?? [],
              )}
            >
              <Popup className={styles.popup}>
                {item.images?.images?.[0]?.bild?.url && (
                  <Image
                    src={item.images.images[0]?.bild?.url ?? ""}
                    className={styles.image}
                  />
                )}
                <div className={styles.title}>{item.name}</div>
                {item.nativeNames && (
                  <div
                    className={styles.description}
                  >{`(${item.nativeNames})`}</div>
                )}
                <div className={styles.link}>
                  <Link to={`/browse/places/${item.id}`}>
                    {i18n.home.map.more}
                  </Link>
                </div>
              </Popup>
            </Marker>
          );
        })}
        <ZoomControl position="bottomright" />
      </MapContainer>
    </div>
  );
};

export default Map;
